import { ApolloClient } from "@apollo/client";
import invariant from "invariant";
import { orderBy } from "lodash";
import { action, makeObservable, observable, override } from "mobx";
import {
  AiChatInterfaceInput,
  AiChatInterfaceSuggestionResponse,
  AiToolFieldAutofill,
  AiToolFinalOutput,
  GenerateStepOutput,
} from "../graphql/aiTools/aiTools.mutation";
import { GetAiToolSteps, GetResourceFromToolStep } from "../graphql/aiTools/aiTools.queries";
import AiToolStep from "../models/AiToolStep";
import BaseStore from "./BaseStore";
import RootStore from "./RootStore";

export default class AiToolStepsStore extends BaseStore<AiToolStep> {
  isLoadingAiToolSteps = false;
  // Track which tool steps have completed generation (stepId -> boolean)
  generationCompleteMap: Map<string, boolean> = new Map();

  constructor(rootStore: RootStore, apolloClient: ApolloClient<any>) {
    super(rootStore, AiToolStep, apolloClient);

    makeObservable(this, {
      isLoadingAiToolSteps: observable,
      generationCompleteMap: observable,
      // Actions
      fetchAiToolSteps: action,
      fetchSingleResource: action,
      markGenerationComplete: action,
      aiToolFieldAutofill: action,
      aiToolGenerateStepOutput: action,
      aiToolFinalOutput: action,
      aiChatInterfaceInput: action,
      aiChatInterfaceSuggestionResponse: action,
      // Computed
      sortedData: override,
    });

    this.rootStore = rootStore;
    this.apolloClient = apolloClient;
  }

  async fetchAiToolSteps(toolId: string) {
    if (!this.rootStore.auth.user || !this.rootStore.auth.user.id) {
      return null;
    }

    this.isLoadingAiToolSteps = true;

    try {
      const res = await this.apolloClient.query({
        query: GetAiToolSteps,
        variables: {
          where: {
            aiTool: {
              id: toolId,
            },
          },
        },
      });

      if (!res.data || !res.data.aiToolSteps) {
        throw new Error("Failed to fetch Data. Please try again.");
      }

      const aiToolSteps = res.data.aiToolSteps;

      aiToolSteps.forEach((entry: any) => {
        console.log("Entry fetched from backend", entry);

        invariant(entry.aiTool, "Entry must have a conversation id.");

        const sanitizedEntry = {
          id: entry.id,
          data: entry.data,
          aiToolId: entry.aiTool.id,
          stepId: entry.stepId,
          createdAt: entry.createdAt,
          updatedAt: entry.updatedAt,
        };

        this.add(sanitizedEntry);
      });
    } catch (e) {
      throw e;
    } finally {
      this.isLoadingAiToolSteps = false;
    }
  }

  /**
   * Fetch a single resource from the backend and update the local store
   * Used for streaming UI - called when resourceReady event is received
   */
  async fetchSingleResource(
    toolId: string,
    stepId: string,
    trackId: string,
    resourceKey: string
  ) {
    try {
      const res = await this.apolloClient.query({
        query: GetResourceFromToolStep,
        variables: {
          toolId,
          stepId,
          trackId,
          resourceKey,
        },
        fetchPolicy: "network-only", // Always fetch fresh data
      });

      if (!res.data || !res.data.getResourceFromToolStep) {
        console.warn("Failed to fetch resource:", { toolId, stepId, trackId, resourceKey });
        return;
      }

      const { content, loading } = res.data.getResourceFromToolStep;

      // Find the existing step and update it with the new resource
      const existingStep = this.sortedData.find(
        (step) => step.aiToolId === toolId && step.stepId === stepId
      );

      if (!existingStep) {
        console.warn("Step not found in store:", { toolId, stepId });
        return;
      }

      // Update the step data with the new resource
      const currentData = existingStep.data || {};
      
      if (trackId === "differentiationAndEnrichment") {
        // Update enrichment resource
        const enrichment = currentData.differentiationAndEnrichment || {};
        enrichment[resourceKey] = content;
        if (!loading) {
          enrichment.loading = false;
        }
        currentData.differentiationAndEnrichment = enrichment;
      } else {
        // Update track-based resource
        const levels = currentData.levels || [];
        const levelIndex = levels.findIndex((l: any) => l.id === trackId);
        if (levelIndex !== -1) {
          levels[levelIndex][resourceKey] = content;
          if (!loading) {
            levels[levelIndex].loading = false;
          }
        }
        currentData.levels = levels;
      }

      // Update the step in the store
      existingStep.data = currentData;
      
      console.log(`Resource ${resourceKey} updated for track ${trackId}`);
    } catch (e) {
      console.error("Error fetching single resource:", e);
    }
  }

  /**
   * Mark a step's generation as complete
   * Called when generationComplete event is received
   */
  markGenerationComplete(toolId: string, stepId: string) {
    const key = `${toolId}-${stepId}`;
    this.generationCompleteMap.set(key, true);
    console.log(`Generation complete for ${key}`);

    // Also update the step data to mark all loading flags as false
    const existingStep = this.sortedData.find(
      (step) => step.aiToolId === toolId && step.stepId === stepId
    );

    if (existingStep && existingStep.data) {
      const data = existingStep.data;
      
      // Mark all levels as not loading
      if (data.levels) {
        data.levels.forEach((level: any) => {
          level.loading = false;
        });
      }
      
      // Mark enrichment as not loading
      if (data.differentiationAndEnrichment) {
        data.differentiationAndEnrichment.loading = false;
      }
      
      // Mark chat as not loading
      if (data.chat) {
        data.chat.loading = false;
      }

      existingStep.data = data;
    }
  }

  /**
   * Check if generation is complete for a given tool and step
   */
  isGenerationComplete(toolId: string, stepId: string): boolean {
    const key = `${toolId}-${stepId}`;
    return this.generationCompleteMap.get(key) ?? false;
  }

  async aiToolFieldAutofill(
    id: string,
    aiToolId: string,
    aiStepId: string,
    fieldId: string
  ) {
    try {
      const res = await this.apolloClient.mutate({
        mutation: AiToolFieldAutofill,
        variables: {
          where: {
            id,
            aiToolId,
            aiStepId,
            fieldId,
          },
        },
      });

      if (!res.data || !res.data.aiToolFieldAutofill) {
        throw new Error("Failed to generate response. Please try again.");
      }
    } catch (e) {
      throw e;
    } finally {
    }
  }

  async aiToolGenerateStepOutput(
    id: string,
    aiToolId: string,
    aiStepId: string
  ) {
    try {
      const res = await this.apolloClient.mutate({
        mutation: GenerateStepOutput,
        variables: {
          where: {
            id,
            aiToolId,
            aiStepId,
          },
        },
      });

      if (!res.data || !res.data.generateStepOutput) {
        throw new Error("Failed to generate response. Please try again.");
      }
    } catch (e) {
      throw e;
    } finally {
    }
  }

  async aiToolFinalOutput(id: string, aiToolId: string, aiStepId: string) {
    try {
      const res = await this.apolloClient.mutate({
        mutation: AiToolFinalOutput,
        variables: {
          where: {
            id,
            aiToolId,
            aiStepId,
          },
        },
      });

      if (!res.data || !res.data.aiToolFinalOutput) {
        throw new Error("Failed to generate response. Please try again.");
      }
    } catch (e) {
      throw e;
    } finally {
    }
  }

  async aiChatInterfaceInput(
    id: string,
    aiToolId: string,
    aiStepId: string,
    query: string
  ) {
    try {
      const res = await this.apolloClient.mutate({
        mutation: AiChatInterfaceInput,
        variables: {
          where: {
            id,
            aiToolId,
            aiStepId,
            query,
          },
        },
      });

      if (!res.data || !res.data.aiChatInterfaceInput) {
        throw new Error("Failed to generate response. Please try again.");
      }

      return res.data.aiChatInterfaceInput;
    } catch (e) {
      throw e;
    } finally {
    }
  }

  async aiChatInterfaceSuggestionResponse(
    id: string,
    aiToolId: string,
    aiStepId: string,
    accept: boolean
  ) {
    try {
      const res = await this.apolloClient.mutate({
        mutation: AiChatInterfaceSuggestionResponse,
        variables: {
          where: {
            id,
            aiToolId,
            aiStepId,
            accept,
          },
        },
      });

      if (!res.data || !res.data.aiChatInterfaceSuggestionResponse) {
        throw new Error("Failed to accept changes. Please try again.");
      }

      return res.data.aiChatInterfaceSuggestionResponse;
    } catch (e) {
      throw e;
    } finally {
    }
  }

  get sortedData(): AiToolStep[] {
    return orderBy(Array.from(this.data.values()), "updatedAt", "desc");
  }
}
